import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import Download2Line from '@/assets/icons/download_2_line.svg?component';
import { Button } from '@/components/atoms/Buttons';
import { Spinner } from '@/components/atoms/Spinner';
import { SvgIcon } from '@/components/atoms/SvgIcon/SvgIcon';
import PageLayout from '@/components/Layout/PageLayout';
import { retryFailedQueries } from '@/components/retryFailedQueries';
import SomethingHappened from '@/components/SomethingHappened';
import { useCage, useCageName } from '@/contexts/cage';
import useCageFilter from '@/contexts/cage-filter-context';
import { useGetReport } from '@/domain/smoltReport';
import { usePDFExport } from '@/hooks/usePDFExport';

import SmoltReportDetailView from './SmoltReportDetailView';

import styles from './SmoltReportDetailContainer.module.scss';

const PDF_CONTAINER_WIDTH = 1410;

interface SmoltReportDetailContainerProps {
    cageId: number;
}

export default function SmoltReportDetailContainer(props: SmoltReportDetailContainerProps) {
    const { cageId } = props;
    const { location } = useCage();
    const cageName = useCageName();
    const { t } = useTranslation();
    const { selectedFishGroupId } = useCageFilter();
    const isFishGroupSelected = selectedFishGroupId != null;

    const { reportId } = useParams<{ reportId?: string | undefined }>();

    const parsedReportId = Number.parseInt(reportId ?? '0', 10);
    const isValidReportId = Number.isFinite(parsedReportId) && parsedReportId > 0;

    const reportQuery = useGetReport(cageId, parsedReportId, {
        enabled: isValidReportId,
    });
    const { data, isLoading, error } = reportQuery;

    const cageTitle = `${location?.name ?? ''} - ${cageName ?? ''}`;
    const detailTitle = `${t('common.labels.report')}: ${isValidReportId ? parsedReportId : (reportId ?? '')}`;
    const title = `${cageTitle} - ${detailTitle}`;

    const pdfFileName = useMemo(() => {
        const idForFile =
            isValidReportId && !isLoading && data.id === parsedReportId ? data.id : parsedReportId;
        return `smolt_report_${idForFile}_${location?.name?.replace(/\s+/g, '') ?? ''}_${cageName?.replace(/\s+/g, '') ?? ''}_${new Date().toISOString().split('T')[0]}.pdf`;
    }, [data.id, isLoading, isValidReportId, location?.name, cageName, parsedReportId]);

    const pdfExportContent = useMemo(
        () => (
            <SmoltReportDetailView
                report={data}
                isLoading={false}
                isPdfExport
                isFishGroupSelected={isFishGroupSelected}
            />
        ),
        [data, isFishGroupSelected]
    );

    const { triggerDownload, isExporting, PDFContainer } = usePDFExport({
        filename: pdfFileName,
        content: pdfExportContent,
        containerWidth: PDF_CONTAINER_WIDTH,
        analyticsExportType: 'smolt_report',
        analyticsReportType: 'smolt',
    });

    const canExportPdf = isValidReportId && !isLoading && data.id === parsedReportId;

    if (error) {
        return (
            <PageLayout title={title}>
                <SomethingHappened {...retryFailedQueries(reportQuery)} />
            </PageLayout>
        );
    }

    return (
        <PageLayout
            title={title}
            actions={
                <Button
                    className={styles.hidePrint}
                    variant={'secondary'}
                    size="medium"
                    onClick={triggerDownload}
                    disabled={!canExportPdf || isExporting}
                    trackingId="smolt_report_export"
                    icon={
                        // The PDF takes a few seconds to build; the spinner says it is under way.
                        isExporting ? (
                            <Spinner size="sm" />
                        ) : (
                            <SvgIcon
                                color="currentColor"
                                icon={Download2Line}
                                size="custom"
                                customSize={18}
                            />
                        )
                    }>
                    {t('common.actions.export')}
                </Button>
            }>
            <PDFContainer />
            <SmoltReportDetailView
                report={data}
                isLoading={isLoading}
                isFishGroupSelected={isFishGroupSelected}
            />
        </PageLayout>
    );
}
